// ==================== TIPOS BASE ====================

export interface MotivoMerma {
  id: number;
  nombre: string;
  descripcion?: string;
  requiere_observacion: boolean;
  activo: boolean;
  orden: number;
  created_at: string;
  updated_at: string;
}

export interface Merma {
  id: number;
  lote_id: number;
  ingrediente_id: number;
  cantidad: number;
  motivo: string;
  observaciones?: string;
  fecha_registro: string;
  usuario_id: number;
  costo_unitario: number;
  costo_total: number;
  estado: 'pendiente' | 'aprobado' | 'rechazado';
  aprobado_por?: number;
  fecha_aprobacion?: string;
  comentario_aprobacion?: string;
  activo: boolean;
  created_at: string;
  updated_at: string;
  // Campos calculados (joins)
  numero_lote?: string;
  ingrediente_nombre?: string;
  categoria_ingrediente?: string;
  unidad_medida?: string;
  usuario_nombre?: string;
  usuario_email?: string;
  aprobador_nombre?: string;
  proveedor_nombre?: string;
  fecha_vencimiento?: string;
}

// ==================== SUGERENCIAS ====================

export interface SugerenciaMerma {
  lote_id: number;
  numero_lote: string;
  ingrediente_id: number;
  ingrediente_nombre: string;
  unidad_medida: string;
  categoria?: string;
  cantidad_disponible: number;
  fecha_vencimiento: string;
  precio_unitario: number;
  proveedor_nombre?: string;
  dias_para_vencimiento: number;
  prioridad: 'alta' | 'media' | 'baja';
  motivo_sugerido: string;
}

// ==================== FORMULARIOS ====================

export interface CrearMermaDTO {
  lote_id: number;
  ingrediente_id: number;
  cantidad: number;
  motivo: string;
  observaciones?: string;
  fecha_registro?: string;
}

export interface CrearMermasMultiplesDTO {
  items: CrearMermaDTO[];
  fecha_registro?: string;
  observaciones_generales?: string;
}

export interface AprobarMermaDTO {
  comentario?: string;
}

export interface RechazarMermaDTO {
  comentario: string;
}

export interface ActualizarMermaDTO {
  cantidad?: number;
  motivo?: string;
  observaciones?: string;
}

// ==================== FILTROS ====================

export interface FiltrosMerma {
  ingredienteId?: number;
  loteId?: number;
  usuarioId?: number;
  motivo?: string;
  estado?: 'pendiente' | 'aprobado' | 'rechazado';
  activo?: boolean;
  fechaDesde?: string;
  fechaHasta?: string;
  page?: number;
  limit?: number;
}

export interface FiltrosSugerencias {
  diasAnticipacion?: number;
  categoriaId?: number;
}

export interface FiltrosEstadisticas {
  fechaDesde?: string;
  fechaHasta?: string;
  tipo?: 'motivo' | 'ingrediente';
}

// ==================== ESTADÍSTICAS ====================

export interface EstadisticaPorMotivo {
  motivo: string;
  total_cantidad: number;
  total_costo: number;
  count: number;
}

export interface EstadisticaPorIngrediente {
  ingrediente_id: number;
  ingrediente_nombre: string;
  total_cantidad: number;
  total_costo: number;
  count: number;
}

// ==================== RESPUESTAS API ====================

export interface MermasResponse {
  success: boolean;
  data: Merma[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface MermaResponse {
  success: boolean;
  data: Merma;
  message?: string;
}

export interface MotivosResponse {
  success: boolean;
  data: MotivoMerma[];
}

export interface SugerenciasResponse {
  success: boolean;
  data: SugerenciaMerma[];
}

export interface EstadisticasResponse {
  success: boolean;
  data: EstadisticaPorMotivo[] | EstadisticaPorIngrediente[];
}

// ==================== UTILIDADES ====================

export const ESTADOS_MERMA = {
  PENDIENTE: 'pendiente' as const,
  APROBADO: 'aprobado' as const,
  RECHAZADO: 'rechazado' as const,
};

export const PRIORIDADES_SUGERENCIA = {
  ALTA: 'alta' as const,
  MEDIA: 'media' as const,
  BAJA: 'baja' as const,
};

export const getEstadoColor = (estado: Merma['estado']) => {
  switch (estado) {
    case 'pendiente':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'aprobado':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'rechazado':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
  }
};

export const getPrioridadColor = (prioridad: SugerenciaMerma['prioridad']) => {
  switch (prioridad) {
    case 'alta':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    case 'media':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
    case 'baja':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
  }
};

export const getEstadoLabel = (estado: Merma['estado']) => {
  switch (estado) {
    case 'pendiente':
      return 'Pendiente';
    case 'aprobado':
      return 'Aprobado';
    case 'rechazado':
      return 'Rechazado';
    default:
      return estado;
  }
};

export const getPrioridadLabel = (prioridad: SugerenciaMerma['prioridad']) => {
  switch (prioridad) {
    case 'alta':
      return 'Urgente';
    case 'media':
      return 'Pronto';
    case 'baja':
      return 'Preventivo';
    default:
      return prioridad;
  }
};
